import QRCode from 'qrcode';
import React from 'react';

import { HttpError } from '../../api/client';
import { endpoints } from '../../api/sharedEndpoints';
import type { LoginChallenge, TelegramWidgetUser } from '../../api/types';
import { BOT_USERNAME } from '../../config/bot';
import { TavernButton } from '../../ui/components';

import './BrowserLogin.css';

const WIDGET_SRC = 'https://telegram.org/js/telegram-widget.js?22';
const POLL_INTERVAL_MS = 2000;

declare global {
  interface Window {
    onTelegramAuth?: (user: TelegramWidgetUser) => void;
  }
}

type QrState =
  | { status: 'loading' }
  | { status: 'waiting'; challenge: LoginChallenge; qrDataUrl: string }
  | { status: 'expired' }
  | { status: 'unavailable' };

type Props = {
  /** Called once the auth cookie is set; the caller loads the session and app data. */
  onLoggedIn: () => void;
};

/**
 * Login for a regular browser (outside the Telegram WebView):
 * the Telegram Login Widget plus a QR / deep-link flow confirmed in the bot.
 */
export function BrowserLogin({ onLoggedIn }: Props) {
  const widgetRef = React.useRef<HTMLDivElement>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [qr, setQr] = React.useState<QrState>({ status: 'loading' });
  const [attempt, setAttempt] = React.useState(0);
  const [signingIn, setSigningIn] = React.useState(false);

  // Keep the latest callback without restarting the widget / polling effects.
  const onLoggedInRef = React.useRef(onLoggedIn);
  React.useEffect(() => {
    onLoggedInRef.current = onLoggedIn;
  }, [onLoggedIn]);

  // Telegram Login Widget
  React.useEffect(() => {
    const container = widgetRef.current;
    if (!container) return;

    window.onTelegramAuth = (user) => {
      setError(null);
      setSigningIn(true);
      endpoints
        .authTelegramWidget(user)
        .then(() => onLoggedInRef.current())
        .catch(() => {
          setSigningIn(false);
          setError('Не удалось войти через Telegram. Попробуйте ещё раз.');
        });
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = WIDGET_SRC;
    script.setAttribute('data-telegram-login', BOT_USERNAME);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '8');
    script.setAttribute('data-request-access', 'write');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    container.appendChild(script);

    return () => {
      delete window.onTelegramAuth;
      container.replaceChildren();
    };
  }, []);

  // QR / deep-link login: create a challenge, then poll until it is confirmed in the bot.
  React.useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async (challenge: LoginChallenge) => {
      if (cancelled) return;
      try {
        const session = await endpoints.pollLoginChallenge(challenge.code);
        if (cancelled) return;
        if (session) {
          onLoggedInRef.current();
          return;
        }
      } catch (e) {
        if (cancelled) return;
        // 410: code expired or already used; 401: login failed. Neither can recover by polling.
        if (e instanceof HttpError && (e.status === 410 || e.status === 401)) {
          setQr({ status: 'expired' });
          return;
        }
        // Network hiccup or rate limit: keep polling until the code expires.
      }
      timer = setTimeout(() => void poll(challenge), POLL_INTERVAL_MS);
    };

    // Hidden tabs have throttled timers: check right away when the user comes back from Telegram.
    let activeChallenge: LoginChallenge | null = null;
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !activeChallenge) return;
      if (timer) clearTimeout(timer);
      void poll(activeChallenge);
    };
    document.addEventListener('visibilitychange', onVisible);

    const start = async () => {
      setQr({ status: 'loading' });
      try {
        const challenge = await endpoints.createLoginChallenge();
        const qrDataUrl = await QRCode.toDataURL(challenge.deepLink, { width: 220, margin: 1 });
        if (cancelled) return;
        activeChallenge = challenge;
        setQr({ status: 'waiting', challenge, qrDataUrl });
        timer = setTimeout(() => void poll(challenge), POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        // 503 = bot is disabled on this environment: only the widget is available.
        setQr(e instanceof HttpError && e.status === 503 ? { status: 'unavailable' } : { status: 'expired' });
      }
    };

    void start();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [attempt]);

  return (
    <div className="browser-login">
      <h2 className="browser-login-title">Вход через Telegram</h2>
      <p className="browser-login-hint">Вы открыли приложение не из Telegram. Войдите с помощью вашего аккаунта.</p>

      {error && (
        <div role="alert" className="welcome-error-alert">
          {error}
        </div>
      )}

      <div ref={widgetRef} className="browser-login-widget" />

      {signingIn && (
        <p role="status" className="browser-login-hint">
          Входим...
        </p>
      )}

      {qr.status !== 'unavailable' && (
        <div className="browser-login-qr">
          <p className="browser-login-hint">Или отсканируйте QR-код в Telegram на телефоне:</p>

          {qr.status === 'loading' && <p className="browser-login-hint">Готовим QR-код...</p>}

          {qr.status === 'waiting' && (
            <>
              <img className="browser-login-qr-img" src={qr.qrDataUrl} alt="QR-код для входа через Telegram" />
              <a className="browser-login-link" href={qr.challenge.deepLink} target="_blank" rel="noopener noreferrer">
                Открыть в Telegram
              </a>
              <p role="status" className="browser-login-hint">
                Нажмите «Start» в боте — вход произойдёт автоматически. Ожидаем подтверждения...
              </p>
            </>
          )}

          {qr.status === 'expired' && (
            <>
              <p className="browser-login-hint">Код устарел или не удалось его создать.</p>
              <TavernButton variant="secondary" onClick={() => setAttempt((n) => n + 1)}>
                Показать новый QR-код
              </TavernButton>
            </>
          )}
        </div>
      )}
    </div>
  );
}
